import { useEffect, JSX } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import NotFound from 'src/NotFound';
import Layout from 'src/Layout';
import {
  SitecoreContext,
  ComponentPropsContext,
  StaticPath,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import { componentBuilder } from 'temp/componentBuilder';
import config from 'temp/config';
import { buildContext } from '@coveo/headless/commerce';
import {
  extractWildcardPaths,
  getPropertyPaths,
  getCoveoSearchHost,
  getEngine,
  Property,
  buildListingUrl,
} from '@greystar/lib';

const SitecorePage = ({
  notFound,
  componentProps,
  layoutData,
  headLinks,
  requestPath,
}: SitecorePageProps): JSX.Element => {
  useEffect(() => {
    // Since Sitecore editors do not support Fast Refresh, need to refresh editor chromes after Fast Refresh finished
    handleEditorFastRefresh();
  }, []);

  const contextController = buildContext(getEngine());
  const { language } = layoutData.sitecore.context;

  useEffect(() => {
    contextController.setView({
      url: `${getCoveoSearchHost()}${requestPath ?? '/'}`,
    });
    contextController.setLanguage(language ?? 'en');
  }, [contextController, requestPath, language]);

  if (notFound || !layoutData.sitecore.route) {
    // Shouldn't hit this (as long as 'notFound' is being returned below), but just to be safe
    return <NotFound />;
  }

  const isEditing = layoutData.sitecore.context.pageEditing;

  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext
        componentFactory={componentBuilder.getComponentFactory({ isEditing })}
        layoutData={layoutData}
        api={{
          edge: {
            contextId: config.sitecoreEdgeContextId,
            edgeUrl: config.sitecoreEdgeUrl,
          },
        }}
      >
        <Layout layoutData={layoutData} headLinks={headLinks} />
      </SitecoreContext>
    </ComponentPropsContext>
  );
};

// This function gets called at build and export time to determine
// pages for SSG ("paths", as tokenized array).
// If we want to attempt to statically generate all pdps at build time
// this function will do it when building in production and with the absence
// of the DISABLE_SSG_FETCH variable.
// Do this with caution as it will results in thousands of requests to the
// dependencies of the pdp page, such as APIM, CMS, Google places, travel time etc.
export const getStaticPaths: GetStaticPaths = async () => {
  let paths: StaticPath[] = [];
  let fallback: boolean | 'blocking' = 'blocking';

  if (process.env.NODE_ENV !== 'development' && !process.env.DISABLE_SSG_FETCH) {
    try {
      // Note: Next.js runs export in production mode
      paths = await getPropertyPaths().then((paths) => {
        return paths.map((path) => {
          return {
            params: { path: path.split('/').filter((p) => p), locale: 'en' },
          };
        });
      });
    } catch (error) {
      console.log('Error occurred while fetching static paths');
      console.log(error);
    }

    fallback = process.env.EXPORT_MODE ? false : fallback;
  }

  return {
    paths,
    fallback,
  };
};

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation (or fallback) is enabled and a new request comes in.
export const getStaticProps: GetStaticProps = async (context) => {
  if (context.params?.path && Array.isArray(context.params.path)) {
    context.params.requestPath = context.params.path;
    context.params.path = extractWildcardPaths(context.params.path);
  }

  const props = await sitecorePagePropsFactory.create(context);

  props.requestPath =
    context.params?.requestPath && Array.isArray(context.params.requestPath)
      ? `/${context.params.requestPath.join('/')}`
      : '/';

  const property = props.layoutData?.sitecore?.context?.property as Property;

  if (property && property.displayOnWebsite === false) {
    if (property.location?.city && property.location?.state) {
      const listingUrl = buildListingUrl({
        city: property.location?.city,
        state: property.location?.state,
        countryCode: property.location?.countryCode ?? '',
      });

      return {
        redirect: {
          destination: listingUrl,
          permanent: false,
        },
      };
    } else {
      return {
        notFound: true,
      };
    }
  }

  const revalidate = process.env.IsrRevalidateSeconds
    ? Number(process.env.IsrRevalidateSeconds)
    : 3600; // 60 minutes

  return {
    props,
    revalidate, // ISR regeneration interval
    notFound: props.notFound, // Returns custom 404 page with a status code of 404 when true
  };
};

export default SitecorePage;
